import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import DataTable from "aldoo-ra/DataTable"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import UserPlatformAPI from "../../../user-api"
import { renderAvatar } from "../../../render-avatar"
import { DevicePhoneMobileIcon } from "@heroicons/react/24/outline"

/**
 * Modal for restoring deleted users
 */
const RestoreUsersModal = ({ isOpen, onClose, onRestore }) => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [deletedUsers, setDeletedUsers] = useState([])

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // Handler for deleting a user forever
  const deleteForeverAction = (user) => {
    showPromptDialog({
      title: "Delete Forever",
      message: `Are you sure you want to permanently delete user "${user.name}" (${user.email?.split('_deleted_')[0] || user.email})? This action cannot be undone.`,
      confirmText: "Delete Forever",
      onConfirm: async () => {
        try {
          await UserPlatformAPI.HardDeleteUsers([user._id])
          showNotification({
            message: "User permanently deleted successfully.",
            className: "bg-admin_primary text-white",
          })
          // Force refresh the restore dialog table
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
          }, 50)
          onRestore?.() // Refresh the main table
        } catch (error) {
          showNotification({
            message: "Failed to delete the user.",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handler for restoring a user
  const restoreAction = async (user) => {
    try {
      await UserPlatformAPI.RestoreUser(user._id)
      showNotification({
        message: "Successfully restored the user.",
        className: "bg-admin_primary text-admin_text_inv",
      })
      // Force refresh the restore dialog table
      setTimeout(() => {
        setRefreshKey((prev) => prev + 1)
      }, 50)
      onRestore?.() // Refresh the main table
    } catch (error) {
      showNotification({
        message: "Failed to restore the user.",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handler for deleting all users forever
  const deleteAllAction = async () => {
    try {
      // Fetch ALL deleted users in batches (no limit)
      let allDeletedUsers = []
      let skip = 0
      const batchSize = 1000
      let hasMore = true

      while (hasMore) {
        const response = await UserPlatformAPI.GetDeletedUsers({
          page: Math.floor(skip / batchSize) + 1,
          itemsPerPage: batchSize,
          search: {},
          sort: { created: -1 }
        })

        const users = response?.data || []

        if (Array.isArray(users) && users.length > 0) {
          allDeletedUsers = allDeletedUsers.concat(users)

          if (users.length < batchSize) {
            hasMore = false
          } else {
            skip += batchSize
          }
        } else {
          hasMore = false
        }
      }

      if (allDeletedUsers.length === 0) {
        showNotification({
          message: "No deleted users to remove.",
          className: "bg-admin_error text-white",
        })
        return
      }

      showPromptDialog({
        title: "Delete All Forever",
        message: `Are you sure you want to permanently delete all ${allDeletedUsers.length} user(s)? This action cannot be undone and there is no way to reverse it.`,
        confirmText: "Delete All Forever",
        onConfirm: async () => {
          try {
            // Delete all users - get all user IDs
            const userIds = allDeletedUsers.map(user => user._id)
            await UserPlatformAPI.HardDeleteUsers(userIds)

            showNotification({
              message: `Successfully deleted ${allDeletedUsers.length} user(s) permanently.`,
              className: "bg-admin_primary text-admin_text_inv",
            })

            // Force refresh the restore dialog table
            setTimeout(() => {
              setRefreshKey((prev) => prev + 1)
            }, 50)
            onRestore?.() // Refresh the main table
          } catch (error) {
            showNotification({
              message: "Failed to delete all users.",
              className: "bg-admin_error text-white",
            })
          }
        },
      })
    } catch (error) {
      showNotification({
        message: "Failed to fetch deleted users.",
        className: "bg-admin_error text-white",
      })
    }
  }

  const formatDate = (date) => {
    if (!date) return "N/A"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getOriginalEmail = (email) => {
    if (!email) return "No email"
    return email.includes('_deleted_') ? email.split('_deleted_')[0] : email
  }

  const getOriginalGoogleId = (googleId) => {
    if (!googleId) return "N/A"
    return googleId.includes('_deleted_') ? googleId.split('_deleted_')[0] : googleId
  }

  // Define columns for the data table
  const tableItemRender = [
    {
      id: "user",
      title: "User",
      headerClassName: "w-[200px]",
      render: (_, user) => (
        <div className="w-full flex items-center space-x-3">
          {/* User Avatar */}
          {renderAvatar(user, "w-10 h-10")}

          <div className="w-0 flex-1">
            <Typography
              variant="admin_label"
              className="truncate my-0"
            >
              {user.name || "Unknown User"}
            </Typography>

            <Tooltip
              display="block"
              content={
                <div className="text-center">
                  <Typography
                    variant="admin_txt1"
                    className="block"
                  >
                    {getOriginalEmail(user.email)}
                  </Typography>

                  <Typography variant="admin_txt2_dim">
                    Click to copy
                  </Typography>
                </div>
              }
            >
              <button
                onClick={async () => {
                try {
                  await navigator.clipboard.writeText(getOriginalEmail(user.email))
                  showNotification({
                    message: "Copied to clipboard",
                    className: "bg-admin_primary text-white"
                  })
                } catch (error) {
                  console.error("Failed to copy:", error)
                }
              }}
                className="w-full truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
              >
                {getOriginalEmail(user.email)}
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      id: "credentials",
      title: "Credentials",
      headerClassName: "w-[150px]",
      render: (_, user) => (
        <div className="text-sm">
          {user.google_id && (
            <div className="flex items-center gap-1 mb-1">
              <span className="text-admin_text_secondary">
                Google:
              </span>

              <Tooltip content={
                <div className="text-center">
                  <div>{getOriginalGoogleId(user.google_id)}</div>
                  <div className="text-xs text-admin_text_secondary mt-1">Click to copy</div>
                </div>
              }>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(getOriginalGoogleId(user.google_id))
                      showNotification({
                        message: "Copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      console.error("Failed to copy:", error)
                    }
                  }}
                  className="max-w-[80px] font-mono text-xs truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                >
                  {getOriginalGoogleId(user.google_id)}
                </button>
              </Tooltip>
            </div>
          )}

          {user.apple_id && (
            <div className="flex items-center gap-1 mb-1">
              <span className="text-admin_text_secondary">
                Apple:
              </span>

              <Tooltip content={
                <div className="text-center">
                  <div>{user.apple_id.includes('_deleted_') ? user.apple_id.split('_deleted_')[0] : user.apple_id}</div>
                  <div className="text-xs text-admin_text_secondary mt-1">Click to copy</div>
                </div>
              }>
                <button
                  onClick={async () => {
                    try {
                      const appleId = user.apple_id.includes('_deleted_') ? user.apple_id.split('_deleted_')[0] : user.apple_id
                      await navigator.clipboard.writeText(appleId)
                      showNotification({
                        message: "Copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      console.error("Failed to copy:", error)
                    }
                  }}
                  className="max-w-[80px] font-mono text-xs truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                >
                  {user.apple_id.includes('_deleted_') ? user.apple_id.split('_deleted_')[0] : user.apple_id}
                </button>
              </Tooltip>
            </div>
          )}

          {user.facebook_id && (
            <div className="flex items-center gap-1">
              <span className="text-admin_text_secondary">
                Facebook:
              </span>

              <Tooltip content={
                <div className="text-center">
                  <div>{user.facebook_id.includes('_deleted_') ? user.facebook_id.split('_deleted_')[0] : user.facebook_id}</div>
                  <div className="text-xs text-admin_text_secondary mt-1">Click to copy</div>
                </div>
              }>
                <button
                  onClick={async () => {
                    try {
                      const facebookId = user.facebook_id.includes('_deleted_') ? user.facebook_id.split('_deleted_')[0] : user.facebook_id
                      await navigator.clipboard.writeText(facebookId)
                      showNotification({
                        message: "Copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      console.error("Failed to copy:", error)
                    }
                  }}
                  className="max-w-[80px] font-mono text-xs truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                >
                  {user.facebook_id.includes('_deleted_') ? user.facebook_id.split('_deleted_')[0] : user.facebook_id}
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      id: "devices",
      title: "Devices",
      headerClassName: "w-[100px] text-center",
      render: (_, user) => (
        <div className="flex items-center justify-center gap-1">
          <DevicePhoneMobileIcon className="w-4 h-4 text-admin_text_secondary" />
          <span className="text-admin_text">
            {user.devices?.length || 0}
          </span>
        </div>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[100px]",
      render: (_, user) => (
        <Typography
          variant="admin_txt1_dim"
          className="whitespace-nowrap"
        >
          {formatDate(user.created)}
        </Typography>
      ),
    },
    {
      id: "restore",
      title: "Restore",
      headerClassName: "w-[100px] text-center",
      render: (_, user) => (
        <Button
          variant="admin_secondary"
          className="w-20 mx-auto"
          onClick={() => restoreAction(user)}
        >
          Restore
        </Button>
      ),
    },
    {
      id: "delete",
      title: "Delete",
      headerClassName: "w-[120px] text-center",
      render: (_, user) => (
        <Button
          variant="admin_error"
          className="whitespace-nowrap mx-auto"
          onClick={() => deleteForeverAction(user)}
        >
          Delete Forever
        </Button>
      ),
    },
  ]

  const headerControls = (
    <div className="absolute top-[45px] right-[350px] xl:right-[410px] flex items-center gap-3">
      <Button
        variant="admin_error"
        onClick={deleteAllAction}
        className="whitespace-nowrap"
      >
        Delete All{deletedUsers.length > 0 ? ` (${deletedUsers.length})` : ''}
      </Button>
    </div>
  )

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[90%] h-full md:h-100vh top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography variant="admin_h3" className="mb-4">
          Restore Deleted Users
        </Typography>

        {/* Data table for displaying deleted users */}
        <div className="h-[75vh]">
          <DataTable
            localUserPreferencesKey="admin.platformAccess.usersRestoreModal"
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={UserPlatformAPI.GetDeletedUsers}
            preferredHeight="75vh"
            itemHeight="h-[80px]"
            headerControls={headerControls}
            styleOverrides={{
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8"
            }}
            searchField="name email google_id apple_id facebook_id _id"
            searchObject={{}}
            sort={{ created: -1 }}
            renders={tableItemRender}
            onDataLoaded={(data) => {
              setDeletedUsers(data || [])
              return data
            }}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <Typography variant="admin_body" className="text-admin_text_secondary">
                  No deleted users found
                </Typography>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}

export default RestoreUsersModal
